import React from 'react';

/**
 * Itemised cost breakdown for expense vouchers: one row per expense, each a name and
 * an amount, with a running total.
 *
 * Shared by the create and edit forms so the two cannot drift — they post the same
 * JSON, and the backend normalises it in one place.
 *
 * Vouchers raised before the breakdown was itemised carry free text instead. Those are
 * shown above the rows rather than discarded or crammed into a row, so whoever is
 * editing can read what was originally written and re-enter it deliberately.
 */

export type CostItem = { name: string; amount: string };

type Props = {
    items: CostItem[];
    onChange: (items: CostItem[]) => void;
    /** Free text from a voucher raised before this was itemised. */
    legacyText?: string | null;
    /** Amount Requested, so a total that disagrees with it can be pointed out. */
    amountRequested?: string | number | null;
    required?: boolean;
};

export const emptyCostItem = (): CostItem => ({ name: '', amount: '' });

export const costItemsTotal = (items: CostItem[]): number =>
    items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

const money = (v: number) =>
    v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function CostBreakdownEditor({ items, onChange, legacyText, amountRequested, required }: Props) {
    const total = costItemsTotal(items);
    const requested = parseFloat(String(amountRequested ?? '')) || 0;

    // Only worth mentioning once both numbers exist — otherwise a half-filled form
    // nags about a mismatch the user is still in the middle of resolving.
    const showMismatch = requested > 0 && total > 0 && Math.abs(total - requested) >= 0.01;

    const update = (index: number, field: keyof CostItem, value: string) => {
        const next = items.map((item, i) => (i === index ? { ...item, [field]: value } : item));
        onChange(next);
    };

    const addRow = () => onChange([...items, emptyCostItem()]);

    // Never drop to zero rows: an empty editor gives nothing to type into and no
    // obvious way back.
    const removeRow = (index: number) => {
        const next = items.filter((_, i) => i !== index);
        onChange(next.length ? next : [emptyCostItem()]);
    };

    return (
        <div className="relative w-full mb-5">
            <label className="block mb-2 text-sm font-semibold text-[#19213D]">
                Expenses {required && <span className="text-red-600">*</span>}
            </label>

            {legacyText && (
                <div className="mb-3 p-3 bg-[#FFF8E6] border-l-4 border-[#E0A800] rounded-r text-sm">
                    <p className="font-medium text-[#8A6D00] mb-1">Previously entered as free text</p>
                    <p className="whitespace-pre-wrap text-[#6B6B6B]">{legacyText}</p>
                    <p className="text-xs text-[#8A6D00] mt-2">
                        Re-enter these as rows below to itemise this voucher. Saving will replace the text above.
                    </p>
                </div>
            )}

            <div className="border border-[#DADADA] rounded-[5px] overflow-hidden">
                <div className="hidden sm:flex bg-[#FAFAFA] text-xs font-semibold text-[#6B6B6B] px-3 py-2">
                    <div className="flex-1">Expense</div>
                    <div className="w-40">Amount</div>
                    <div className="w-10" />
                </div>

                {items.map((item, index) => (
                    <div key={index} className="flex flex-col sm:flex-row gap-2 sm:gap-0 px-3 py-2 border-t border-[#EFEFEF]">
                        <div className="flex-1 sm:pr-2">
                            <input
                                type="text"
                                value={item.name}
                                onChange={(e) => update(index, 'name', e.target.value)}
                                placeholder="What was it spent on?"
                                className="bg-white border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] block w-full p-2 placeholder:text-[#DADADA] placeholder:text-[12px]"
                            />
                        </div>
                        <div className="w-full sm:w-40 sm:pr-2">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.amount}
                                onChange={(e) => update(index, 'amount', e.target.value)}
                                placeholder="0.00"
                                className="bg-white border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] block w-full p-2 placeholder:text-[#DADADA] placeholder:text-[12px]"
                            />
                        </div>
                        <div className="w-full sm:w-10 flex items-center">
                            <button
                                type="button"
                                onClick={() => removeRow(index)}
                                title="Remove this expense"
                                className="text-[#B0B0B0] hover:text-[#E04343] text-lg leading-none px-2"
                            >
                                &times;
                            </button>
                        </div>
                    </div>
                ))}

                <div className="flex items-center justify-between px-3 py-2 border-t border-[#EFEFEF] bg-[#FAFAFA]">
                    <button
                        type="button"
                        onClick={addRow}
                        className="text-[#E04343] text-sm font-semibold hover:underline"
                    >
                        + Add expense
                    </button>

                    <div className="text-right">
                        <span className="text-xs text-[#6B6B6B] mr-2">Total</span>
                        <span className="text-[15px] font-semibold text-[#19213D]">{money(total)}</span>
                    </div>
                </div>
            </div>

            {showMismatch && (
                <p className="text-xs text-[#E04343] mt-2">
                    These expenses total {money(total)}, but Amount Requested is {money(requested)}
                    {' '}(a difference of {money(Math.abs(total - requested))}).
                </p>
            )}
        </div>
    );
}

export default CostBreakdownEditor;
